import { useState, useEffect, useCallback } from "react";
import { Delete, Fingerprint, LockKeyhole, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth, detectDevice } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "del", "0", "ok"];

export default function LockScreen() {
  const { login } = useAuth();
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);

  const submit = useCallback(async (value) => {
    if (value.length < 4 || busy) return;
    setBusy(true); setError("");
    try {
      await login(value);
      toast.success("تم التحقق. مرحباً بك أيها المالك");
    } catch (e) {
      setError(errMsg(e));
      setShake(true); setTimeout(() => setShake(false), 450);
      setPin("");
    } finally { setBusy(false); }
  }, [login, busy]);

  const press = useCallback((k) => {
    if (k === "del") return setPin((p) => p.slice(0, -1));
    if (k === "ok") return submit(pin);
    if (pin.length >= 12) return;
    setPin((p) => p + k);
  }, [pin, submit]);

  useEffect(() => {
    const onKey = (e) => {
      if (/^[0-9]$/.test(e.key)) press(e.key);
      else if (e.key === "Backspace") press("del");
      else if (e.key === "Enter") press("ok");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [press]);

  return (
    <div className="relative min-h-screen overflow-hidden grain" data-testid="lock-screen">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1739588589478-928d9f1f0f8c?crop=entropy&cs=srgb&fm=jpg&q=60&w=1800')] bg-cover bg-center opacity-[0.12]" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#080C14]/40 via-[#080C14]/80 to-[#080C14]" />
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-10">
        <div className="fade-up w-full max-w-sm">
          <div className="flex items-center justify-between mb-10">
            <div>
              <p className="text-xs font-mono tracking-[0.3em] text-cyan-300/80">AQL4.COM</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-50 mt-1">مصنع المصانع الأم</h1>
              <p className="text-sm text-slate-400 mt-1">الدخول المعتمد — المالك فقط</p>
            </div>
            <div className="h-14 w-14 rounded-2xl glass grid place-items-center text-cyan-300 shadow-[0_0_40px_rgba(0,240,255,0.15)]">
              <LockKeyhole size={26} />
            </div>
          </div>

          <div className={`glass rounded-3xl p-6 sm:p-8 ${shake ? "shake" : ""}`}>
            <input
              data-testid="pin-input-field"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 12))}
              onKeyDown={(e) => e.stopPropagation()}
              className="sr-only"
              aria-label="الرمز السري"
            />
            <div className="flex justify-center gap-3 mb-8 min-h-[16px]" data-testid="pin-dots">
              {Array.from({ length: Math.max(6, pin.length) }).map((_, i) => (
                <span key={i} className={`h-3.5 w-3.5 rounded-full transition-all duration-200 ${i < pin.length ? "bg-cyan-300 shadow-[0_0_12px_rgba(0,240,255,0.8)] scale-110" : "bg-white/10"}`} />
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3" dir="ltr">
              {KEYS.map((k) => (
                <button
                  key={k}
                  data-testid={k === "ok" ? "pin-keypad-submit" : k === "del" ? "pin-keypad-delete" : `pin-keypad-button-${k}`}
                  onClick={() => press(k)}
                  disabled={busy}
                  className={`h-16 rounded-2xl text-xl font-semibold transition-all duration-150 active:scale-95 focus:outline-none focus:ring-2 focus:ring-cyan-400/50
                    ${k === "ok" ? "bg-cyan-400 text-slate-950 hover:bg-cyan-300 shadow-[0_0_30px_rgba(0,240,255,0.35)]" : k === "del" ? "bg-white/5 text-slate-300 hover:bg-white/10" : "bg-white/[0.06] text-slate-100 hover:bg-white/10"}`}
                >
                  {k === "del" ? <Delete className="mx-auto" size={22} /> : k === "ok" ? <ShieldCheck className="mx-auto" size={24} /> : k}
                </button>
              ))}
            </div>
            {error && <p data-testid="pin-error" className="mt-5 text-center text-sm text-red-400">{error}</p>}
            <button
              data-testid="biometric-login-button"
              onClick={() => toast.info("مفتاح الأمان (Passkey) سيتم تفعيله في المرحلة التالية")}
              className="mt-6 w-full flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-cyan-300 transition-colors"
            >
              <Fingerprint size={18} /> الدخول بمفتاح الأمان
            </button>
          </div>
          <p className="mt-6 text-center text-xs text-slate-500 font-mono" data-testid="device-label">{detectDevice()} · جلسة مستقلة لهذا الجهاز</p>
        </div>
      </div>
    </div>
  );
}
